import { Sparkle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useResolveSculpt } from "~/api/queries";
import type { SculptLatest } from "~/api/types";
import { DocumentEditor } from "~/features/documents/editor/DocumentEditor";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";

export interface SculptProposalCardProps {
  documentId: string;
  latest: SculptLatest;
  canUpdate: boolean;
}

/**
 * A staged sculpt awaiting review. Accept applies through the same engine that would have
 * (with drift re-verified server-side); reject and discard are kept distinct because
 * "reviewed and judged wrong" and "dismissed unread" are different facts about the sculptor.
 */
export function SculptProposalCard({ documentId, latest, canUpdate }: SculptProposalCardProps) {
  const { t } = useTranslation();
  const resolve = useResolveSculpt();

  // A drift-minted restore row is reviewed via reject/discard only — the server refuses accept.
  const acceptable = canUpdate && latest.op !== "revertRestore";

  const act = (action: "accept" | "reject" | "discard") => {
    resolve.mutate(
      { documentId, opId: latest.opId, action },
      {
        onSuccess: (outcome) => {
          if (action === "accept" && outcome.status === "degraded") {
            toast.warning(
              t("The document changed underneath — the proposal was refreshed for another look"),
            );
          } else if (action === "accept") {
            toast.success(t("Proposal applied"));
          }
        },
      },
    );
  };

  return (
    <section
      aria-label={t("AI proposal")}
      className="rounded-md border border-accent/40 bg-bg-secondary p-4"
    >
      <div className="flex items-center gap-2">
        <Sparkle size={16} className="text-accent" aria-hidden />
        <Text variant="small" className="font-medium text-text">
          {latest.op === "revertRestore" ? t("Proposed restore") : t("AI proposal")}
        </Text>
        {latest.thinker.thinkerName ? (
          <Text variant="small" className="text-text-tertiary">
            {latest.thinker.thinkerName}
          </Text>
        ) : null}
        {latest.status === "degraded" ? (
          <Text variant="small" className="text-warning">
            {t("Partial — the document changed mid-sculpt")}
          </Text>
        ) : null}
      </div>

      {latest.proposal?.summary ? (
        <Text variant="small" className="mt-1 text-text-secondary">
          {latest.proposal.summary}
        </Text>
      ) : null}

      <div className="mt-3 max-h-80 overflow-y-auto rounded-md border border-divider bg-bg p-3">
        <DocumentEditor
          markdown={latest.proposal?.markdown ?? ""}
          ariaLabel={t("Proposed content")}
        />
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={resolve.isPending}
          onClick={() => act("discard")}
        >
          {t("Discard")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={resolve.isPending}
          onClick={() => act("reject")}
        >
          {t("Reject")}
        </Button>
        {acceptable ? (
          <Button
            variant="primary"
            size="sm"
            disabled={resolve.isPending}
            onClick={() => act("accept")}
          >
            {t("Accept")}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
